// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaAdminRequestStatus,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  InputCheckbox,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatDateRange,
  getPreviousRegionSeasonId,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  regattaStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaAdminRequestsSection from '../../sections/RegattasPageSections/RegattaAdminRequestsSection';
import RegattaAdminProposesSection from '../../sections/RegattasPageSections/RegattaAdminProposesSection';
import RegattaMinimalFields from '../../fields/RegattaMinimalFields';
import RegattaSuperFields from '../../fields/RegattaSuperFields';
import ThQty from '../../common/ThQty';
import InputRegionSeason from '../../form/InputRegionSeason';

// Services
import Services from '../../../services';

// Enums
import {
  regattaStatusClass,
} from '../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $GetRegattasAction,
  $RedirectAction,
  $SetActiveRegattaIdAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  getRegattas: $GetRegattasAction;
  redirect: $RedirectAction;
  setActiveRegattaId: $SetActiveRegattaIdAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Regatta'>>;
  loading: boolean;
  referenceData: Array<$Model<'Regatta'>>;
  resourceData: Array<$Model<'Regatta'>>;
};

const pageId = adminPages.regattasPage;

class RegattasPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      referenceData: [],
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.initialGetData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionSeasonId !== null && isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        const models = ['Regatta', 'RegattaAdminRequest'];

        if (main.activeRegionSeasonId === data.RegionSeasonId) {
          if (data.modelName === 'Regatta') {
            this.getData();
          }

          if (_.includes(
            models,
            data.modelName,
          )) {
            this.getResourceData();
          }
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegatta({
            RegionSeasonId,
          });
          const previousRegionSeasonId = getPreviousRegionSeasonId({
            regions: main.regions,
            RegionSeasonId,
          });
          const referenceData = previousRegionSeasonId ? await Services.Api.getResourceRegatta({
            RegionSeasonId: previousRegionSeasonId,
          }) : [];

          await this.setStateAsync({
            data,
            loading: false,
            referenceData,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          const resourceData = await Services.Api.getResourceRegatta({
            RegionSeasonId,
          });

          await this.setStateAsync({
            resourceData,
          });
        }
      },
    });
  }

  async initialGetData(): Promise<void> {
    await this.getData();
    await this.getResourceData();
  }

  isSuperAdmin(): boolean {
    const {
      auth,
    } = this.props;

    return auth.user !== null && auth.user.Super === true;
  }

  renderHeaderRequestButton() {
    const {
      auth,
      main,
      t,
    } = this.props;
    const {
      data,
      resourceData,
    } = this.state;

    if (auth.user !== null) {
      const newItem: $ModelUpsertParams<'RegattaAdminRequest'> = {
        Email: auth.user.Email,
        Name: auth.user.Name,
        RegattaId: null,
        Status: RegattaAdminRequestStatus.propose,
      };

      const filteredResourceData = resourceData.filter((regatta: $Model<'Regatta'>) => !_.find(
        data,
        {
          id: regatta.id,
        },
      ));

      const RegionSeasonId = main.activeRegionSeasonId;

      if (RegionSeasonId !== null) {
        const region = getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId,
        });

        const regionSeason = getRegionSeasonByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId,
        });

        return (
          <FormDialog
            fields={(
              <>
                <div className={'d-flex flex-row w-100 align-items-center justify-content-between mb-1'}>
                  <h4 className={'h4 mb-0'}>
                    {`${region.Name}: ${regionSeason.Name}`}
                  </h4>
                  <small className={'text-muted lh-1'}>
                    <span>
                      {`${t('Found items')}: `}
                    </span>
                    <span className={'text-bold'}>
                      {filteredResourceData.length}
                    </span>
                  </small>
                </div>
                {filteredResourceData.length === 0 ? (
                  <EmptyTable />
                ) : (
                  <hr />
                )}
                {filteredResourceData.map((resourceItem: $Model<'Regatta'>) => {
                  const date: string = formatDateRange({
                    end: resourceItem.EndDate,
                    shortDateFormat: region.ShortDateFormat,
                    start: resourceItem.StartDate,
                  });

                  return (
                    <InputCheckbox
                      description={`${date}, ${resourceItem.Location}`}
                      key={resourceItem.id}
                      label={resourceItem.Name}
                      name={resourceItem.id}
                    />
                  );
                })}
              </>
            )}
            id={'request-regatta-control-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const actions: Array<Promise<unknown>> = [];

                Object.keys(values).forEach((RegattaId: string) => {
                  if (values[RegattaId]) {
                    const params: $ModelUpsertParams<'RegattaAdminRequest'> = {
                      RegattaId,
                      ...newItem,
                    };

                    const action = Services.Api.upsertRegattaAdminRequest(params);

                    actions.push(action);
                  }
                });

                await Promise.all(actions);
              },
            })}
            overflowControl
            subTitle={t('Select regattas you want to control')}
            title={t('Regattas')}
          >
            <Button
              className={'mr-1'}
              color={'warning'}
              id={'request-regatta-control-button'}
              size={'sm'}
            >
              {t('I want to control regatta')}
            </Button>
          </FormDialog>
        );
      }
    }

    return null;
  }

  renderHeaderCreateButton() {
    const {
      auth,
      getRegattas,
      main,
      redirect,
      setActiveRegattaId,
      t,
    } = this.props;
    const {
      referenceData,
    } = this.state;

    const RegionSeasonId = main.activeRegionSeasonId;

    if (RegionSeasonId !== null && auth.user !== null) {
      const newItem: $ModelUpsertParams<'Regatta'> = {
        BowNoEnabled: false,
        Canceled: false,
        Consent: null,
        Content: null,
        CountryEnabled: false,
        EndDate: moment().set({
          hour: 17,
          minute: 0,
        }).toDate(),
        GeoLocation: null,
        GeoLocationLat: null,
        GeoLocationLng: null,
        GroupChangeEnabled: false,
        Highlight: false,
        Image: null,
        Invoice: null,
        InvoiceAdminId: null,
        ListEnabled: true,
        Location: '',
        Lock: false,
        Name: '',
        OrganizerContact: null,
        OrganizerEmail: auth.user ? auth.user.Email : '',
        OrganizerName: null,
        PageEnabled: true,
        PreventDuplicateRegistrationNo: false,
        Public: false,
        ReferenceId: null,
        RegionSeasonId,
        RegistrationBirthdateField: false,
        RegistrationClubField: false,
        RegistrationContent: null,
        RegistrationEmailField: false,
        RegistrationEnabled: true,
        RegistrationEndDate: null,
        RegistrationHarborField: false,
        RegistrationMembershipField: false,
        RegistrationStartDate: null,
        RegistrationTshirtField: false,
        RegistrationWeightField: false,
        SailNoEnabled: false,
        Slug: null,
        StartDate: moment().set({
          hour: 9,
          minute: 0,
        }).toDate(),
        SubmitProtestEnabled: false,
      };

      return (
        <FormDialog
          fields={(
            <RegattaMinimalFields
              item={newItem}
              referenceData={referenceData}
            />
          )}
          id={'create-regatta-dialog'}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Regatta'>>({
                ...newItem,
                ...values,
              });

              const newRegatta = await Services.Api.createRegatta(params);

              if (main.activeRegionId !== null && RegionSeasonId !== null) {
                await getRegattas({
                  RegionSeasonId,
                });

                await setActiveRegattaId({
                  RegattaId: newRegatta.id,
                });

                redirect({
                  pathname: '/regatta/settings',
                });
              }
            },
          })}
          subTitle={t('New')}
          title={t('Regatta')}
        >
          <Button
            color={'info'}
            id={'create-regatta-button'}
            size={'sm'}
            title={t('Add new regatta')}
          >
            {t('I want to create regatta')}
          </Button>
        </FormDialog>
      );
    }

    return null;
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          iconClass={'icon-flag'}
          rightButtons={(
            <Row>
              <Col
                className={'mb-1 mb-lg-0'}
                lg={6}
              >
                <InputRegionSeason />
              </Col>
              <Col
                className={'d-flex flex-row justify-content-end text-nowrap'}
                lg={6}
              >
                {this.renderHeaderRequestButton()}
                {this.renderHeaderCreateButton()}
              </Col>
            </Row>
          )}
          title={t('Regattas')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'Regatta'>) {
    const {
      main,
      redirect,
      setActiveRegattaId,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        {this.isSuperAdmin() === true && (
          <>
            <FormDialog
              fields={(
                <RegattaSuperFields item={dataItem} />
              )}
              id={'super-user-regatta-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Regatta'>>({
                    ...dataItem,
                    ...values,
                  });

                  await Services.Api.updateRegatta(params);
                },
              })}
              subTitle={t('Super user control')}
              title={t('Regatta')}
            >
              <Button
                className={'mr-1'}
                color={'secondary'}
                id={'super-user-regatta-button'}
                outline
                size={'sm'}
              >
                <em className={'fa icon-lock'} />
              </Button>
            </FormDialog>
            <DeleteDialog
              content={t('Do you really want to delete?')}
              id={`delete-regatta-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelDeleteButton={t('Delete')}
              onAction={() => defaultDeleteItem({
                action: async () => {
                  await Services.Api.deleteRegatta(dataItem);

                  if (main.activeRegattaId === id) {
                    await setActiveRegattaId({
                      RegattaId: null,
                    });
                  }
                },
              })}
              subTitle={dataItem.Name}
              title={t('Regatta')}
            >
              <Button
                className={'mr-3'}
                color={'danger'}
                id={`delete-regatta-button-${id}`}
                outline
                size={'sm'}
                title={t('Delete')}
              >
                <em className={'fa fa-trash'} />
              </Button>
            </DeleteDialog>
          </>
        )}
        <Button
          className={'mr-1'}
          color={'warning'}
          id={`redirect-regatta-page-button-${id}`}
          onClick={async () => {
            await setActiveRegattaId({
              RegattaId: id,
            });

            redirect({
              pathname: '/',
            });
          }}
          outline
          size={'sm'}
        >
          {t('Edit')}
        </Button>
        <CommonDialog
          content={(
            <CardBody>
              {t('Do you really want to cancel regatta?')}
            </CardBody>
          )}
          id={`cancel-regatta-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          onAction={() => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Regatta'>>({
                ...dataItem,
                Canceled: true,
              });

              await Services.Api.updateRegatta(params);
            },
          })}
          title={t('Need confirmation')}
        >
          <Button
            className={'mr-1'}
            color={'danger'}
            id={`cancel-regatta-dialog-${id}`}
            outline
            size={'sm'}
          >
            {t('Cancel')}
          </Button>
        </CommonDialog>
      </div>
    );
  }

  renderRegattas() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $Model<'Regatta'>) => (
          <tr
            key={dataItem.id}
            style={publicBorder(dataItem.Public)}
          >
            <td>
              <span className={`badge rounded-pill ${regattaStatusClass[dataItem.Status]}`}>
                {t(regattaStatusLabel[dataItem.Status])}
              </span>
            </td>
            <td>{dataItem.Name}</td>
            <TdDate value={dataItem.StartDate} />
            <TdDate value={dataItem.EndDate} />
            <td>{dataItem.Location}</td>
            <td>
              {this.renderButtons(dataItem)}
            </td>
          </tr>
        ))}
      </>
    );
  }

  renderContent() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {data.length === 0 ? (
            <>
              <Alert color={'warning m-3'}>
                {t('You do not have any regattas on the list, you can either create a new regatta or request control of a regatta that is already present on the particular region season')}
              </Alert>
              <EmptyTable />
            </>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>{t('Status')}</th>
                  <th>{t('Name')}</th>
                  <th>{t('Start date')}</th>
                  <th>{t('End date')}</th>
                  <th>{t('Location')}</th>
                  <ThQty value={data.length} />
                </tr>
              </thead>
              <tbody>
                {this.renderRegattas()}
              </tbody>
            </Table>
          )}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          <RegattaAdminRequestsSection id={adminSections[pageId].requests} />
          <RegattaAdminProposesSection id={adminSections[pageId].proposes} />
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattasPageView;
